import axios from "axios";

//  SSE client built on fetch with auth headers

const RETRY_BASE_MS = 1000;
const RETRY_MAX_MS = 10_000;
const DEFAULT_MAX_ATTEMPTS = 5;

export type SseFrame = {
  id: string | null;
  event: string;
  data: string;
};

export class SseHttpError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = "SseHttpError";
    this.status = status;
  }
}

export class SseDisconnectedError extends Error {
  constructor(message = "Lost the connection to the stream.") {
    super(message);
    this.name = "SseDisconnectedError";
  }
}

/** Retry only transient statuses. */
const isRetryableStatus = (status: number) => status === 429 || status >= 500;

const parseFrame = (raw: string): SseFrame | null => {
  let id: string | null = null;
  let event = "message";
  const data: string[] = [];

  for (const line of raw.split("\n")) {
    if (!line || line.startsWith(":")) continue;

    const colon = line.indexOf(":");
    const field = colon === -1 ? line : line.slice(0, colon);
    let value = colon === -1 ? "" : line.slice(colon + 1);
    if (value.startsWith(" ")) value = value.slice(1);

    if (field === "id") id = value;
    else if (field === "event") event = value;
    else if (field === "data") data.push(value);
  }

  if (data.length === 0) return null;
  return { id, event, data: data.join("\n") };
};

export const createSseParser = (onFrame: (frame: SseFrame) => void) => {
  let buffer = "";

  return {
    push(chunk: string) {
      buffer += chunk.replace(/\r\n|\r/g, "\n");

      let separator = buffer.indexOf("\n\n");
      while (separator !== -1) {
        const raw = buffer.slice(0, separator);
        buffer = buffer.slice(separator + 2);
        const frame = parseFrame(raw);
        if (frame) onFrame(frame);
        separator = buffer.indexOf("\n\n");
      }
    },
  };
};

const resolveUrl = (path: string): URL =>
  new URL(path, axios.defaults.baseURL ?? window.location.origin);

const defaultCredentials = (): "omit" | "same-origin" | "include" => {
  const base = axios.defaults.baseURL ?? "";
  return base.includes("leap.lucernahealth") || base.includes("://localhost")
    ? "include"
    : "same-origin";
};

const authHeaders = (): Record<string, string> => {
  const header = axios.defaults.headers.common?.Authorization;
  return typeof header === "string" && header ? { Authorization: header } : {};
};

const wait = (ms: number, signal: AbortSignal): Promise<void> =>
  new Promise((resolve) => {
    let timer = 0;
    const onAbort = () => {
      window.clearTimeout(timer);
      resolve();
    };
    timer = window.setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    signal.addEventListener("abort", onAbort, { once: true });
  });

export type StreamSseOptions = {
  path: string;
  signal: AbortSignal;
  onFrame: (frame: SseFrame) => void;
  isComplete: () => boolean;
  lastEventId?: string | null;
  onRetry?: (attempt: number, delayMs: number) => void;
  maxAttempts?: number;
  resumeQueryParam?: string | null;
  headers?: Record<string, string>;
  credentials?: "omit" | "same-origin" | "include";
};

export const streamSse = async ({
  path,
  signal,
  onFrame,
  isComplete,
  lastEventId = null,
  onRetry,
  maxAttempts = DEFAULT_MAX_ATTEMPTS,
  resumeQueryParam = "last_event_id",
  headers,
  credentials,
}: StreamSseOptions): Promise<void> => {
  let cursor = lastEventId;
  let attempt = 0;

  const readOnce = async () => {
    const url = resolveUrl(path);
    if (cursor && resumeQueryParam) {
      url.searchParams.set(resumeQueryParam, cursor);
    }

    const response = await fetch(url.toString(), {
      method: "GET",
      signal,
      cache: "no-store",
      credentials: credentials ?? defaultCredentials(),
      headers: {
        Accept: "text/event-stream",
        ...authHeaders(),
        ...(headers ?? {}),
      },
    });

    if (!response.ok) {
      throw new SseHttpError(
        response.status,
        `Stream request failed with HTTP ${response.status}.`,
      );
    }
    if (!response.body) {
      throw new SseHttpError(response.status, "Stream response had no body.");
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    const parser = createSseParser((frame) => {
      if (frame.id) cursor = frame.id;
      attempt = 0;
      onFrame(frame);
    });

    try {
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        parser.push(decoder.decode(value, { stream: true }));
        if (isComplete() || signal.aborted) return;
      }
      parser.push(decoder.decode());
    } finally {
      reader.cancel().catch(() => undefined);
    }
  };

  for (;;) {
    if (signal.aborted) return;

    try {
      await readOnce();
      if (isComplete() || signal.aborted) return;
    } catch (error) {
      if (signal.aborted) return;
      if (error instanceof SseHttpError && !isRetryableStatus(error.status)) {
        throw error;
      }
      if (attempt >= maxAttempts) throw error;
    }

    attempt += 1;
    if (attempt > maxAttempts) throw new SseDisconnectedError();

    const delay = Math.min(RETRY_BASE_MS * 2 ** (attempt - 1), RETRY_MAX_MS);
    onRetry?.(attempt, delay);
    await wait(delay, signal);
  }
};
